import type { JSONSchema } from "./sync/types";

// OutputField is one part of a task's output, as it is read: words, a list
// of items, or JSON for anything else.
export type OutputField = { key: string; label: string; hint?: string } & ({ kind: "text"; text: string } | { kind: "list"; items: string[] } | { kind: "json"; json: string });

const scalar = (v: unknown): v is string | number | boolean => ["string", "number", "boolean"].includes(typeof v);

// outputFields lays out an object output by its schema's properties, with
// their titles and descriptions, then any other fields by name. Stored
// schemas lose their properties' order, so the required ones come first, in
// the order they are required, and JSON last. null when the output is not
// an object, to show it as JSON.
export function outputFields(schema: JSONSchema | null, output: unknown): OutputField[] | null {
  if (output === null || typeof output !== "object" || Array.isArray(output)) return null;
  const o = output as Record<string, unknown>;
  const props = schema?.properties ?? {};
  const keys = [...new Set([...(schema?.required ?? []), ...Object.keys(props), ...Object.keys(o)])];
  const fields = keys
    .filter((k) => o[k] !== undefined && o[k] !== null)
    .map((key): OutputField => {
      const v = o[key];
      const head = { key, label: props[key]?.title ?? key, hint: props[key]?.description };
      if (scalar(v)) return { ...head, kind: "text", text: String(v) };
      if (Array.isArray(v) && v.length > 0 && v.every(scalar)) return { ...head, kind: "list", items: v.map(String) };
      return { ...head, kind: "json", json: JSON.stringify(v, null, 2) };
    });
  return [...fields.filter((f) => f.kind !== "json"), ...fields.filter((f) => f.kind === "json")];
}
